import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import MaterialReactTable from 'material-react-table'
import { MRT_Localization_PT } from 'material-react-table/locales/pt'
import {
  Badge,
  Button,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from 'reactstrap'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import 'react-tabs/style/react-tabs.css'
import { downloadFile } from '../../../utils/fc'
import { ButtonLiberarPagamento } from '../../../components/Modals/ModalContrFornInfo/ModalContrFornInfo'
import { formatBudgetMoney } from './budgetDashboardHelpers'
import {
  fetchCompraCotacoes,
  fetchContratoEventos,
  fetchContratosByOrcItem,
  fetchFornecedor,
  fetchPedidosByOrcItem,
  getEventoVencimento,
  getFornecedorNome,
} from './budgetItemModalApi'
import './BudgetItemModal.scss'

const mrtCompact = {
  muiTableBodyCellProps: { sx: { fontSize: '11px' } },
  muiTableHeadCellProps: { sx: { fontSize: '12px', fontWeight: 'normal', padding: '8px' } },
  enableDensityToggle: false,
  enableColumnFilters: false,
}

function fmtDate(v) {
  if (!v) return '—'
  return moment(v, 'YYYY-MM-DD').format('DD/MM/YYYY')
}

function ContratoResumo({ contrato }) {
  if (!contrato) return null
  return (
    <div className="budget-item-detail-grid">
      <div><span className="label">Número</span><strong>{contrato.contrForn_num || '—'}</strong></div>
      <div><span className="label">Status</span><strong>{contrato.contrFornStatus_nome || '—'}</strong></div>
      <div><span className="label">Tipo</span><strong>{contrato.contrFornTp_nome || '—'}</strong></div>
      <div><span className="label">Fornecedor</span><strong>{getFornecedorNome(contrato)}</strong></div>
      <div><span className="label">Valor global</span><strong>{formatBudgetMoney(contrato.contrForn_vlrGlobal)}</strong></div>
      <div><span className="label">Assinatura</span><strong>{fmtDate(contrato.contrForn_dtAssin)}</strong></div>
      <div><span className="label">Vigência</span><strong>{`${fmtDate(contrato.contrForn_dtVigIni)} — ${fmtDate(contrato.contrForn_dtVigFim)}`}</strong></div>
      <div className="full"><span className="label">Descrição</span><strong>{contrato.contrForn_desc || '—'}</strong></div>
      {contrato.contrForn_obj && (
        <div className="full"><span className="label">Objeto</span><strong>{contrato.contrForn_obj}</strong></div>
      )}
    </div>
  )
}

function PedidoResumo({ pedido }) {
  if (!pedido) return null
  return (
    <div className="budget-item-detail-grid">
      <div><span className="label">Número</span><strong>{pedido.pedidoCompra_num || '—'}</strong></div>
      <div><span className="label">Status</span><strong>{pedido.compraStatus_nome || '—'}</strong></div>
      <div><span className="label">Solicitante</span><strong>{pedido.pf_nomeExibicao || '—'}</strong></div>
      <div><span className="label">Valor limite</span><strong>{formatBudgetMoney(pedido.pedidoCompra_vlrLimite)}</strong></div>
      <div><span className="label">Solicitação</span><strong>{fmtDate(pedido.pedidoCompra_dtSolicitacao)}</strong></div>
      <div><span className="label">Limite entrega</span><strong>{fmtDate(pedido.pedidoCompra_dtLimiteEntrega)}</strong></div>
      <div><span className="label">Vigência</span><strong>{`${fmtDate(pedido.pedidoCompra_dtVigIni)} — ${fmtDate(pedido.pedidoCompra_dtVigFim)}`}</strong></div>
      <div className="full"><span className="label">Descrição</span><strong>{pedido.pedidoCompra_desc || '—'}</strong></div>
    </div>
  )
}

function ContratoEventosTab({
  contrato,
  eventos,
  loading,
  onRefresh,
}) {
  const [fornecedor, setFornecedor] = useState({})
  const [listaParcelas, setListaParcelas] = useState(eventos)

  useEffect(() => {
    setListaParcelas(eventos)
  }, [eventos])

  useEffect(() => {
    if (!contrato?.contrForn_parteTab || !contrato?.contrForn_parteId) return
    fetchFornecedor(contrato.contrForn_parteTab, contrato.contrForn_parteId).then(setFornecedor)
  }, [contrato])

  const updateOneEvento = useCallback(async (eventoId) => {
    const refreshed = await fetchContratoEventos(contrato.contrForn_id)
    setListaParcelas(refreshed)
    onRefresh?.()
    return refreshed.find(e => Number(e.contrFornEvento_id) === Number(eventoId))
  }, [contrato?.contrForn_id, onRefresh])

  if (loading) {
    return <div className="text-center py-4"><Spinner size="sm" /></div>
  }

  if (!listaParcelas.length) {
    return <p className="text-muted mb-0">Nenhum evento cadastrado neste contrato.</p>
  }

  return (
    <Table responsive size="sm" className="mb-0">
      <thead>
        <tr>
          <th>#</th>
          <th>Descrição</th>
          <th>Vencimento</th>
          <th>Valor</th>
          <th>Liberado</th>
          <th>Pago</th>
          <th>Evidências</th>
          <th>Ações</th>
        </tr>
      </thead>
      <tbody>
        {listaParcelas.map(ev => {
          const liberado = Number(ev.contrFornEvento_aprovLiberado) === 1
          const pago = Boolean(ev.bdro_dtQuit)
          const canLiberar = ![7, 8, 1].includes(Number(contrato.contrFornStatus_id)) && !liberado && !pago
          return (
            <tr key={ev.contrFornEvento_id}>
              <td>{ev.contrFornEvento_num || '—'}</td>
              <td>{ev.contrFornEvento_desc || '—'}</td>
              <td>{fmtDate(getEventoVencimento(ev))}</td>
              <td>{formatBudgetMoney(ev.contrFornEvento_vlr)}</td>
              <td>{liberado ? <Badge color="success" pill>Sim</Badge> : <Badge color="secondary" pill>Não</Badge>}</td>
              <td>{pago ? fmtDate(ev.bdro_dtQuit) : '—'}</td>
              <td>
                <div className="d-flex flex-wrap" style={{ gap: 4 }}>
                  {ev.arquivo_caminho_evidencia && (
                    <Button size="sm" color="link" className="p-0" onClick={() => downloadFile(ev.arquivo_caminho_evidencia)}>
                      Evidência
                    </Button>
                  )}
                  {ev.arquivo_caminho_nota_fiscal && (
                    <Button size="sm" color="link" className="p-0" onClick={() => downloadFile(ev.arquivo_caminho_nota_fiscal)}>
                      NF
                    </Button>
                  )}
                  {!ev.arquivo_caminho_evidencia && !ev.arquivo_caminho_nota_fiscal && '—'}
                </div>
              </td>
              <td>
                {canLiberar && (
                  <ButtonLiberarPagamento
                    fornecedor={fornecedor}
                    setFornecedor={setFornecedor}
                    contrFornEventoData={ev}
                    getEventos={async () => {
                      const r = await fetchContratoEventos(contrato.contrForn_id)
                      setListaParcelas(r)
                      onRefresh?.()
                    }}
                    listaParcelas={listaParcelas}
                    contrFornData={contrato}
                    updateOneEvento={updateOneEvento}
                    setListaParcelas={setListaParcelas}
                  />
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}

function PedidoCotacoesTab({ pedido, cotacoes, loading }) {
  if (loading) return <div className="text-center py-4"><Spinner size="sm" /></div>
  if (!cotacoes.length) return <p className="text-muted mb-0">Nenhuma cotação registrada.</p>

  return (
    <Table responsive size="sm" className="mb-0">
      <thead>
        <tr>
          <th>Fornecedor</th>
          <th>Status</th>
          <th>Valor</th>
          <th>Envio</th>
          <th>Resposta</th>
          <th>Contrato</th>
        </tr>
      </thead>
      <tbody>
        {cotacoes.map(c => (
          <tr key={c.compraCotacao_id}>
            <td>{c.fornecedor_nome || '—'}</td>
            <td>{c.compraStatus_nome || '—'}</td>
            <td>{formatBudgetMoney(c.compraCotacao_vlr)}</td>
            <td>{fmtDate(c.compraCotacao_dtEnvio)}</td>
            <td>{fmtDate(c.compraCotacao_dtResposta)}</td>
            <td>{c.contrForn_num || (c.contrForn_id ? `#${c.contrForn_id}` : '—')}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

function DetailPane({
  sourceType,
  selectedItem,
  eventos,
  cotacoes,
  loadingDetail,
  onRefreshEventos,
}) {
  const [detailTab, setDetailTab] = useState(0)

  if (!selectedItem) {
    return <p className="text-muted p-3 mb-0">Selecione um item na lista à esquerda.</p>
  }

  const isContrato = sourceType === 'contrato'

  return (
    <div className="budget-item-detail-pane">
      <h6 className="mb-3">
        {isContrato
          ? (selectedItem.contrForn_desc || selectedItem.contrForn_num)
          : (selectedItem.pedidoCompra_desc || selectedItem.pedidoCompra_num)}
      </h6>
      <Tabs selectedIndex={detailTab} onSelect={setDetailTab}>
        <TabList>
          <Tab>Informações</Tab>
          <Tab>{isContrato ? 'Eventos / parcelas' : 'Cotações'}</Tab>
        </TabList>
        <TabPanel>
          {isContrato ? <ContratoResumo contrato={selectedItem} /> : <PedidoResumo pedido={selectedItem} />}
        </TabPanel>
        <TabPanel>
          {isContrato ? (
            <ContratoEventosTab
              contrato={selectedItem}
              eventos={eventos}
              loading={loadingDetail}
              onRefresh={onRefreshEventos}
            />
          ) : (
            <PedidoCotacoesTab pedido={selectedItem} cotacoes={cotacoes} loading={loadingDetail} />
          )}
        </TabPanel>
      </Tabs>
    </div>
  )
}

const BudgetItemModal = ({
  isOpen,
  onClose,
  orcItemId,
  itemLabel,
  onOpenCreateContrato,
}) => {
  const [loading, setLoading] = useState(false)
  const [listTab, setListTab] = useState(0)
  const [view, setView] = useState('list')
  const [sourceType, setSourceType] = useState('contrato')
  const [contratos, setContratos] = useState([])
  const [pedidos, setPedidos] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [eventos, setEventos] = useState([])
  const [cotacoes, setCotacoes] = useState([])
  const [loadingDetail, setLoadingDetail] = useState(false)

  const loadLists = useCallback(async () => {
    if (!orcItemId) return
    setLoading(true)
    try {
      const [cRows, pRows] = await Promise.all([
        fetchContratosByOrcItem(orcItemId),
        fetchPedidosByOrcItem(orcItemId),
      ])
      setContratos(cRows)
      setPedidos(pRows)
    } finally {
      setLoading(false)
    }
  }, [orcItemId])

  useEffect(() => {
    if (!isOpen || !orcItemId) return
    setView('list')
    setSelectedId(null)
    setListTab(0)
    loadLists()
  }, [isOpen, orcItemId, loadLists])

  const loadDetail = useCallback(async (type, id) => {
    setLoadingDetail(true)
    try {
      if (type === 'contrato') {
        setEventos(await fetchContratoEventos(id))
        setCotacoes([])
      } else {
        setCotacoes(await fetchCompraCotacoes(id))
        setEventos([])
      }
    } finally {
      setLoadingDetail(false)
    }
  }, [])

  const openDetail = (type, id) => {
    setSourceType(type)
    setSelectedId(id)
    setView('detail')
    loadDetail(type, id)
  }

  const sidebarItems = sourceType === 'contrato' ? contratos : pedidos

  const selectedItem = useMemo(() => {
    if (!selectedId) return null
    const list = sourceType === 'contrato' ? contratos : pedidos
    const key = sourceType === 'contrato' ? 'contrForn_id' : 'pedidoCompra_id'
    return list.find(i => Number(i[key]) === Number(selectedId)) || null
  }, [selectedId, sourceType, contratos, pedidos])

  const contratoColumns = useMemo(() => [
    { accessorKey: 'contrForn_num', header: 'Número', size: 90 },
    { accessorKey: 'contrForn_desc', header: 'Descrição', size: 200 },
    {
      id: 'fornecedor',
      header: 'Fornecedor',
      accessorFn: row => getFornecedorNome(row),
      size: 140,
    },
    {
      accessorKey: 'contrForn_vlrGlobal',
      header: 'Valor',
      size: 100,
      Cell: ({ cell }) => formatBudgetMoney(cell.getValue()),
    },
    { accessorKey: 'contrFornStatus_nome', header: 'Status', size: 100 },
  ], [])

  const pedidoColumns = useMemo(() => [
    { accessorKey: 'pedidoCompra_num', header: 'Número', size: 90 },
    { accessorKey: 'pedidoCompra_desc', header: 'Descrição', size: 200 },
    { accessorKey: 'pf_nomeExibicao', header: 'Solicitante', size: 140 },
    {
      accessorKey: 'pedidoCompra_vlrLimite',
      header: 'Valor limite',
      size: 100,
      Cell: ({ cell }) => formatBudgetMoney(cell.getValue()),
    },
    { accessorKey: 'compraStatus_nome', header: 'Status', size: 100 },
  ], [])

  const handleBackToList = () => {
    setView('list')
    setSelectedId(null)
  }

  const handleSidebarSelect = (type, id) => {
    setSourceType(type)
    setSelectedId(id)
    loadDetail(type, id)
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" className="budget-item-modal">
      <ModalHeader toggle={onClose}>
        <div className="d-flex flex-wrap align-items-start justify-content-between w-100" style={{ gap: 8 }}>
          <div>
            {view === 'detail' && (
              <Button color="link" className="p-0 mr-2 align-baseline" onClick={handleBackToList}>
                ← Voltar
              </Button>
            )}
            <span>{itemLabel || `Item #${orcItemId}`}</span>
            <div className="text-muted small font-weight-normal mt-1">
              Contratos e pedidos de compra vinculados ao item orçamentário
            </div>
          </div>
          {orcItemId && (
            <Button
              color="primary"
              size="sm"
              onClick={() => onOpenCreateContrato?.(orcItemId)}
            >
              Criar Contrato
            </Button>
          )}
        </div>
      </ModalHeader>
      <ModalBody className="p-0">
        {loading && view === 'list' && (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        )}

        {!loading && view === 'list' && (
          <div className="p-3">
            <Tabs selectedIndex={listTab} onSelect={setListTab}>
              <TabList>
                <Tab>{`Contratos (${contratos.length})`}</Tab>
                <Tab>{`Pedidos de compra (${pedidos.length})`}</Tab>
              </TabList>
              <TabPanel>
                <div className="d-flex justify-content-end mb-2">
                  {orcItemId && (
                    <Button
                      color="outline-primary"
                      size="sm"
                      onClick={() => onOpenCreateContrato?.(orcItemId)}
                    >
                      Criar Contrato
                    </Button>
                  )}
                </div>
                {contratos.length === 0 ? (
                  <p className="text-muted mb-0 py-3">Nenhum contrato neste item.</p>
                ) : (
                  <MaterialReactTable
                    columns={contratoColumns}
                    data={contratos}
                    getRowId={row => String(row.contrForn_id)}
                    localization={MRT_Localization_PT}
                    enablePagination={contratos.length > 10}
                    muiTableBodyRowProps={({ row }) => ({
                      onClick: () => openDetail('contrato', row.original.contrForn_id),
                      sx: { cursor: 'pointer' },
                    })}
                    initialState={{ density: 'compact', pagination: { pageSize: 10 } }}
                    {...mrtCompact}
                    muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                  />
                )}
              </TabPanel>
              <TabPanel>
                {pedidos.length === 0 ? (
                  <p className="text-muted mb-0 py-3">Nenhum pedido de compra neste item.</p>
                ) : (
                  <MaterialReactTable
                    columns={pedidoColumns}
                    data={pedidos}
                    getRowId={row => String(row.pedidoCompra_id)}
                    localization={MRT_Localization_PT}
                    enablePagination={pedidos.length > 10}
                    muiTableBodyRowProps={({ row }) => ({
                      onClick: () => openDetail('pedido', row.original.pedidoCompra_id),
                      sx: { cursor: 'pointer' },
                    })}
                    initialState={{ density: 'compact', pagination: { pageSize: 10 } }}
                    {...mrtCompact}
                    muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                  />
                )}
              </TabPanel>
            </Tabs>
          </div>
        )}

        {view === 'detail' && (
          <Row noGutters className="budget-item-detail-layout">
            <Col md={4} className="budget-item-sidebar border-right">
              <div className="p-2 border-bottom small font-weight-bold text-muted">
                {sourceType === 'contrato' ? 'Contratos' : 'Pedidos de compra'}
              </div>
              <div className="budget-item-sidebar-list">
                {sidebarItems.map(item => {
                  const id = sourceType === 'contrato' ? item.contrForn_id : item.pedidoCompra_id
                  const label = sourceType === 'contrato'
                    ? (item.contrForn_num || item.contrForn_desc)
                    : (item.pedidoCompra_num || item.pedidoCompra_desc)
                  const sub = sourceType === 'contrato'
                    ? getFornecedorNome(item)
                    : (item.compraStatus_nome || '')
                  const active = Number(selectedId) === Number(id)
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`budget-item-sidebar-item${active ? ' active' : ''}`}
                      onClick={() => handleSidebarSelect(sourceType, id)}
                    >
                      <div className="title">{label}</div>
                      <div className="sub">{sub}</div>
                    </button>
                  )
                })}
              </div>
            </Col>
            <Col md={8}>
              <DetailPane
                sourceType={sourceType}
                selectedItem={selectedItem}
                eventos={eventos}
                cotacoes={cotacoes}
                loadingDetail={loadingDetail}
                onRefreshEventos={() => loadDetail('contrato', selectedId)}
              />
            </Col>
          </Row>
        )}
      </ModalBody>
    </Modal>
  )
}

export default BudgetItemModal
